import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI, Notification } from '../api/api';
import './NotificationsDropdown.css';

interface NotificationsDropdownProps {
  onClose: () => void;
  onNotificationClick?: () => void;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ onClose, onNotificationClick }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Загружаем уведомления при открытии dropdown
    loadNotifications();
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Проверяем, что клик не на кнопке уведомлений
      const notificationsButton = target.closest('.notifications-button');
      if (notificationsButton) {
        // Клик на кнопке уведомлений - не закрываем
        return;
      }
      
      // Проверяем, что клик не внутри dropdown
      if (!dropdownRef.current || !dropdownRef.current.contains(target)) {
        onClose();
        return;
      }

      // Если клик внутри dropdown, проверяем, что это не элемент уведомления
      const notificationItem = target.closest('.notification-item');
      if (notificationItem) {
        // Клик на элементе уведомления - не закрываем, onMouseDown обработает
        return;
      }

      // Клик внутри dropdown, но не на уведомлении (например, на header) - не закрываем
    };

    // Используем небольшую задержку, чтобы dropdown успел открыться
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsAPI.getAll();
      setNotifications(data);
    } catch (error) {
      console.error('Ошибка при загрузке уведомлений:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    console.log('Notification clicked:', notification);
    console.log('Task ID:', notification.task_id, 'Type:', typeof notification.task_id);

    // Переходим к задаче, если есть task_id
    if (notification.task_id !== null && notification.task_id !== undefined) {
      // Помечаем как прочитанное перед навигацией
      if (notification.is_read === 0) {
        try {
          console.log('Marking notification as read:', notification.id);
          await notificationsAPI.markAsRead(notification.id);
          setNotifications(prev => 
            prev.map(n => n.id === notification.id ? { ...n, is_read: 1 } : n)
          );
          if (onNotificationClick) {
            onNotificationClick();
          }
          console.log('Notification marked as read successfully');
        } catch (error) {
          console.error('Ошибка при обновлении уведомления:', error);
          // Продолжаем навигацию даже если не удалось пометить как прочитанное
        }
      }

      // Закрываем выпадающее меню перед навигацией
      onClose();

      // Используем navigate для навигации без перезагрузки страницы
      // Для HashRouter используем объект с searchParams
      console.log('Navigating to task:', notification.task_id);
      const taskId = Number(notification.task_id);
      if (!isNaN(taskId)) {
        navigate({
          pathname: '/my-tasks',
          search: `?task=${taskId}`
        });
      } else {
        console.error('Invalid task_id:', notification.task_id);
      }
    } else {
      console.warn('Notification has no task_id:', notification);
      // Если нет task_id, просто помечаем как прочитанное и закрываем
      if (notification.is_read === 0) {
        try {
          await notificationsAPI.markAsRead(notification.id);
          setNotifications(prev => 
            prev.map(n => n.id === notification.id ? { ...n, is_read: 1 } : n)
          );
          if (onNotificationClick) {
            onNotificationClick();
          }
        } catch (error) {
          console.error('Ошибка при обновлении уведомления:', error);
        }
      }
      onClose();
    }
  };

  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем всплытие события
    try {
      await notificationsAPI.markAllAsRead();
      const unreadCount = notifications.filter(n => n.is_read === 0).length;
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      // Обновляем счетчик в Navbar
      if (onNotificationClick) {
        // Вызываем колбэк для каждого непрочитанного уведомления
        for (let i = 0; i < unreadCount; i++) {
          onNotificationClick();
        }
      }
    } catch (error) {
      console.error('Ошибка при обновлении уведомлений:', error);
    }
  };

  const unreadCount = notifications.filter(n => n.is_read === 0).length;

  return (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <div className="notifications-header">
        <h3>Уведомления</h3>
        {unreadCount > 0 && (
          <button 
            onClick={handleMarkAllAsRead} 
            className="mark-all-read-btn"
            type="button"
          >
            Отметить все как прочитанные
          </button>
        )}
      </div>
      <div className="notifications-list">
        {loading ? (
          <div className="notifications-loading">Загрузка...</div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">Нет уведомлений</div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item ${notification.is_read === 0 ? 'unread' : ''}`}
              onMouseDown={(e) => {
                // Используем onMouseDown, чтобы обработать клик до handleClickOutside
                e.preventDefault();
                e.stopPropagation(); // Останавливаем всплытие, чтобы handleClickOutside не сработал
                // Вызываем обработчик
                handleNotificationClick(notification);
              }}
            >
              <div className="notification-content">
                <div className="notification-title">{notification.title}</div>
                <div className="notification-message">{notification.message}</div>
                {notification.task_title && (
                  <div className="notification-task">Задача: {notification.task_title}</div>
                )}
                <div className="notification-time">
                  {new Date(notification.created_at).toLocaleString('ru-RU')}
                </div>
              </div>
              {notification.is_read === 0 && <div className="notification-dot"></div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsDropdown;
